import React, { useContext, useEffect } from "react";
import './NotificationDropdown.css';
import { CheckCircle, Loader2, Bell, RefreshCw, Trash2, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";
import { LanguageContext } from "../../constant/LanguageContext";
import NotificationItem from "./NotificationItem";
import { useAuth } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import { resolveNotificationNavigation, applyViewSwitch } from "../../utils/notificationNavigation";

const NotificationDropdown = ({ isOpen, onClose, isMobile }) => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    pagination, 
    fetchNotifications, 
    markAllAsRead,
    deleteAllNotifications
  } = useNotifications();
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth() || {};
  const { profile } = useProfile() || {};

  const isAr = language === "ar";
  const t = {
    notifications: isAr ? "الإشعارات" : "Notifications",
    markAllRead: isAr ? "تحديد  " : "Mark all as read",
    deleteAll: isAr ? "مسح الكل" : "Delete all",
    noNotifications: isAr ? "لا توجد إشعارات" : "No notifications",
    loading: isAr ? "جاري التحميل..." : "Loading...",
    loadMore: isAr ? "تحميل المزيد" : "Load more",
  };

  const handleNotificationClick = (notification) => {
    // Resolve the destination — the VIEW that owns the notification's page
    // for this account, the shell path, and the query params that let the
    // page focus the exact record (open the leave/complaint/document modal,
    // highlight the row, open the chat, ...). Marking as read already
    // happened in NotificationItem's own click handler.
    const role = String(user?.role || localStorage.getItem("role") || "user").toLowerCase();
    const target = resolveNotificationNavigation(notification, {
      role,
      permissions,
      pathname: location.pathname,
      selfCode: profile?.infinityCode || localStorage.getItem("infinityCode") || null,
    });
    if (target) {
      // Synchronize the active view (storage + switcher event) BEFORE
      // navigating so the destination shell mounts in the right view and
      // the ViewTypeSwitcher never goes out of sync.
      applyViewSwitch(target.view);
      navigate(target.path);
    }
    onClose();
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    if (pagination.hasMore && !loading) {
      fetchNotifications(pagination.page + 1, true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="notification-backdrop" onClick={onClose}>
      <div className={`notification-dropdown ${isAr ? "rtl" : "ltr"} ${isMobile ? "mobile" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="notification-header">
        <h3 className="notification-title">{t.notifications}</h3>
        <div className="notification-header-actions">
          <button 
            className="refresh-btn" 
            onClick={(e) => { e.stopPropagation(); fetchNotifications(1, false); }}
            title={isAr ? "تحديث" : "Refresh"}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "spin" : ""} />
          </button>
          {notifications.length > 0 && (
            <button 
              className="delete-all-btn" 
              onClick={(e) => { 
                e.stopPropagation(); 
                if(window.confirm(isAr ? "هل أنت متأكد من مسح جميع الإشعارات؟" : "Are you sure you want to delete all notifications?")) {
                  deleteAllNotifications();
                }
              }}
              title={t.deleteAll}
              style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <Trash2 size={14} />
            </button>
          )}
          {unreadCount > 0 && (
            <button
              className="mark-all-btn"
              onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
              title={t.markAllRead}
            >
              <CheckCircle size={14} />
            </button>
          )}
          {isMobile && (
            <button
              className="notification-close-btn"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              title={isAr ? "إغلاق" : "Close"}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="notification-body">
        {loading && pagination.page === 1 ? (
          <div className="notification-loading" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
            <Loader2 size={24} className="spin" />
            <span>{t.loading}</span>
          </div>
        ) : notifications.length === 0 && !loading ? (
          <div className="notification-empty">
            <Bell size={32} className="empty-icon" />
            <p>{t.noNotifications}</p>
          </div>
        ) : (
          <div className="notification-list">
            {notifications.map((notification) => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                onClick={() => handleNotificationClick(notification)} 
              />
            ))}
            
            {pagination.hasMore && (
              <button 
                className="load-more-btn" 
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? <Loader2 size={16} className="spin" /> : t.loadMore}
              </button>
            )}
            
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default NotificationDropdown;
